import React, { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { useQuery, useQueryClient } from 'react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Globe, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { projectService } from '@/services/projectService'
import api from '@/services/api'

const PRESET_COLORS = [
  '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444',
  '#F97316', '#EAB308', '#22C55E', '#14B8A6',
  '#06B6D4', '#6366F1', '#84CC16', '#F43F5E',
]

const ProjectForm = ({ open, onOpenChange, project }) => {
  const queryClient = useQueryClient()
  const isEditing = !!project

  const { data: usersData } = useQuery(
    'team-members',
    async () => { const r = await api.get('/users/team-members'); return r.data },
    { enabled: open }
  )
  const users = usersData?.data?.users || []

  const [form, setForm] = useState({
    name: '',
    key: '',
    description: '',
    lead_id: '',
    color: '#3B82F6',
    visibility: 'public',
  })
  const [keyTouched, setKeyTouched] = useState(false)
  const [loading, setLoading] = useState(false)

  const autoGenerateKey = (name) => {
    const words = name.trim().split(/\s+/).filter(Boolean)
    let key = words.map(w => w[0]).join('').toUpperCase().slice(0, 5)
    if (key.length < 2) key = name.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 3)
    return key
  }

  useEffect(() => {
    if (project) {
      setForm({
        name: project.name || '',
        key: project.key || '',
        description: project.description || '',
        lead_id: project.lead_id ? String(project.lead_id) : '',
        color: project.color || '#3B82F6',
        visibility: project.visibility || 'public',
      })
      setKeyTouched(true)
    } else {
      setForm({ name: '', key: '', description: '', lead_id: '', color: '#3B82F6', visibility: 'public' })
      setKeyTouched(false)
    }
  }, [project, open])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) return toast.error('Project name is required')
    if (!form.key || form.key.length < 2) return toast.error('Project key must be 2-5 uppercase letters')

    setLoading(true)
    try {
      const payload = {
        name: form.name.trim(),
        key: form.key,
        description: form.description.trim() || null,
        lead_id: form.lead_id ? parseInt(form.lead_id) : null,
        color: form.color,
        visibility: form.visibility,
      }

      if (isEditing) {
        await projectService.updateProject(project.id, payload)
        toast.success('Project updated')
      } else {
        await projectService.createProject(payload)
        toast.success('Project created')
      }

      queryClient.invalidateQueries('projects')
      onOpenChange(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Project' : 'New Project'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => {
                const name = e.target.value
                setForm(f => ({
                  ...f,
                  name,
                  ...(!keyTouched ? { key: autoGenerateKey(name) } : {})
                }))
              }}
              placeholder="Project name"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="key">Project Key *</Label>
            <Input
              id="key"
              value={form.key}
              onChange={(e) => {
                setKeyTouched(true)
                setForm(f => ({ ...f, key: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5) }))
              }}
              placeholder="e.g. JF"
              maxLength={5}
              className="uppercase w-32 font-mono"
            />
            <p className="text-xs text-muted-foreground">2-5 uppercase letters. Used in task IDs (e.g. {form.key || 'XX'}-1)</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What is this project about?"
              rows={3}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Lead</Label>
            <Select
              value={form.lead_id}
              onValueChange={(v) => setForm(f => ({ ...f, lead_id: v === 'none' ? '' : v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="No lead assigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No lead</SelectItem>
                {users.map((u) => {
                  const name = u.employee
                    ? `${u.employee.first_name} ${u.employee.last_name}`
                    : u.email
                  return <SelectItem key={u.id} value={String(u.id)}>{name}</SelectItem>
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Visibility</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, visibility: 'public' }))}
                className={cn(
                  "flex items-center gap-2 flex-1 rounded-md border px-3 py-2 text-sm transition-colors",
                  form.visibility === 'public'
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-input text-muted-foreground hover:bg-muted"
                )}
              >
                <Globe className="h-4 w-4" />
                Public
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, visibility: 'private' }))}
                className={cn(
                  "flex items-center gap-2 flex-1 rounded-md border px-3 py-2 text-sm transition-colors",
                  form.visibility === 'private'
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-input text-muted-foreground hover:bg-muted"
                )}
              >
                <Lock className="h-4 w-4" />
                Private
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {form.visibility === 'public'
                ? 'Visible to all team members'
                : 'Only invited members can access'}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, color: c }))}
                  className="w-7 h-7 rounded-full border-2 transition-all"
                  style={{
                    backgroundColor: c,
                    borderColor: form.color === c ? '#000' : 'transparent',
                    transform: form.color === c ? 'scale(1.15)' : 'scale(1)',
                  }}
                />
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ProjectForm
